import type { Messages } from "@/i18n/locales/en";
import { legalMessagesFi } from "@/i18n/locales/legalFiData";
import { pagesMessagesFi } from "@/i18n/locales/pagesFiData";

export const fiMessages = {
  meta: {
    description:
      "WasOK — skannaa tuotteita, tallenna arvioita ja muistiinpanoja, muista mikä kannatti ostaa.",
    title: "WasOK — Muista mikä kannatti ostaa",
  },
  header: {
    navFeatures: "Ominaisuudet",
    navHowItWorks: "Näin se toimii",
    navPrivacy: "Tietosuoja",
    navUseCases: "Käyttötapaukset",
    download: "Lataa",
    openMenu: "Avaa valikko",
    closeMenu: "Sulje valikko",
    languageAria: "Kieli",
  },
  hero: {
    badge: "📱 Saatavilla nyt Androidille",
    title: "Muista mikä kannatti ostaa.",
    subtitle:
      "Skannaa tuotteita, tallenna arvioita ja muistiinpanoja — ja muista heti, oliko tuote todella hyvä.",
    googlePlay: "Lataa Google Playsta",
    watchDemo: "Katso demo",
    privacyLine: "Kaikki tiedot pysyvät laitteellasi.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skannaa muistaaksesi",
    snackCard: "Herkullista!",
    coffeeCard: "Liian karvas",
    scrollHint: "Vieritä tutustuaksesi",
  },
  problems: {
    heading: "Tuttu tilanne?",
    subheading:
      "Olemme kaikki olleet siinä — hyllyn edessä yrittämässä muistaa, pidimmekö tuotteesta viimeksi.",
    notedAgo: "✓ Merkitsit tämän 3 kuukautta sitten",
    items: [
      {
        question: "Mistähän suklaasta pidin oikeasti?",
        note: "Täydellinen makeus",
      },
      {
        question: "Miksi lopetin tämän shampoon ostamisen?",
        note: "Kuivatti hiukset",
      },
      {
        question: "Oliko tämä kahvi hyvä vai pettymys?",
        note: "Liian karvas",
      },
    ],
  },
  howItWorks: {
    heading: "Näin se toimii",
    subheading:
      "Neljä yksinkertaista vaihetta, jotta et enää unohda tuote mielipiteitäsi.",
    stepPrefix: "Vaihe",
    steps: [
      {
        title: "Skannaa minkä tahansa tuotteen viivakoodi",
        description:
          "Nopea ja helppo skannaus kameralla. Toimii kaikilla viivakoodeilla.",
      },
      {
        title: "Tallenna arviot, kommentit ja kuvat",
        description:
          "Arvioi tuotteita, lisää muistiinpanoja, liitä kuvia ja kirjaa hintoja sekä paikkoja.",
      },
      {
        title: "Muista kokemuksesi heti",
        description:
          "Skannaa sama tuote myöhemmin ja näe aiempi arviosi sekä muistiinpanot.",
      },
      {
        title: "Luo älykkäitä ostoslistoja",
        description:
          "Luo ostoslistoja tuotteista, joihin jo luotat ja joista pidät.",
      },
    ],
  },
  privacy: {
    heading: "Ostoshistoriasi pysyy sinun.",
    body: "WasOK auttaa sinua muistamaan kokeilemiasi tuotteita samalla kun arviosi, muistiinpanosi, kuvasi ja ostoslistasi tallennetaan paikallisesti laitteellesi.",
    trustBanner:
      "💚 Rakennettu ostoshistoriasi ympärille — ei tietojesi keräämisen ympärille.",
    bullets: [
      "Tiliä ei tarvita",
      "Pilvisynkronointia ei tarvita",
      "Paikallinen tallennus ensin",
      "Varmuuskopiot hallinnassasi",
      "Valinnainen viivakoodihaku tuote­tiedoille.",
      "Mainosrahoitteinen Google AdMobilla",
    ],
  },
  features: {
    heading: "Kaikki mitä tarvitset",
    subheading:
      "Tehokkaat ominaisuudet, jotka tekevät tuotteiden seurannasta yksinkertaista ja yksityistä.",
    items: [
      {
        title: "Viivakoodiskanneri",
        description: "Nopea ja tarkka viivakoodiskannaus mille tahansa tuotteelle.",
      },
      {
        title: "Tuotearviot",
        description: "Arvioi tuotteita 1–5 tähdellä muistaaksesi laadun.",
      },
      {
        title: "Tuotekuvat",
        description: "Liitä kuvia muistaaksesi pakkauksen tai yksityiskohdat.",
      },
      {
        title: "Kategoriat",
        description: "Järjestä tuotteet omiin kategorioihin.",
      },
      {
        title: "Kaupan seuranta",
        description: "Muista, mistä ostit kunkin tuotteen.",
      },
      {
        title: "Hintakirjaus",
        description: "Seuraa hintoja löytääksesi parhaat tarjoukset.",
      },
      {
        title: "Ostoslistat",
        description: "Luo listoja suosikkituotteistasi.",
      },
      {
        title: "Offline-käyttö",
        description: "Toimii täydellisesti ilman internetyhteyttä.",
      },
      {
        title: "Paikallinen tallennus",
        description: "Kaikki tietosi tallennetaan turvallisesti laitteellesi.",
      },
    ],
  },
  useCases: {
    heading: "Täydellinen jokaiseen hyllyyn",
    subheading:
      "Olitpa ostamassa ruokaa, kauneustuotteita tai kodintarvikkeita — WasOK auttaa muistamaan.",
    cards: [
      {
        emoji: "🍫",
        title: "Naposteltavat & herkut",
        description: "Muista, mitkä makeiset olivat oikeasti hyviä.",
      },
      {
        emoji: "☕",
        title: "Kahvi & juomat",
        description: "Seuraa kahveja, jotka kannattaa ostaa uudelleen.",
      },
      {
        emoji: "💄",
        title: "Kosmetiikka & ihonhoito",
        description: "Pidä muistiinpanoja ihoreaktioista.",
      },
      {
        emoji: "🧹",
        title: "Kodintarvikkeet",
        description: "Vältä tuotteiden uudelleenostamista, jotka pettivät.",
      },
    ],
  },
  screenshots: {
    heading: "Kaunis & intuitiivinen",
    subheading:
      "Huolella suunniteltu käyttöliittymä, joka tekee tuotteiden seurannasta miellyttävää.",
    items: [
      { title: "Skanneri" },
      { title: "Tuotetiedot" },
      { title: "Arvion syöttö" },
      { title: "Haku & historia" },
      { title: "Ostoslista" },
    ],
  },
  cta: {
    heading: "Ostomuisti arkeen.",
    body: "Lopeta niiden tuotteiden unohtaminen, jotka kannatti ostaa. Ala shoppailla fiksummin tänään.",
    googlePlay: "Lataa Google Playsta",
    scanQr: "Skannaa ladataksesi",
    tagline: "Yksityinen · Offline ensin · Yksinkertainen",
  },
  footer: {
    tagline: "Muista mikä kannatti ostaa.",
    linksHeading: "Linkit",
    privacyPolicy: "Tietosuojakäytäntö",
    terms: "Käyttöehdot",
    contact: "Yhteystiedot",
    linkFaq: "UKK",
    linkLicenses: "Avoimen lähdekoodin lisenssit",
    linkAdvertising: "Mainonta & monetisointi",
    downloadHeading: "Lataa",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Kaikki oikeudet pidätetään. Tehty 💚 fiksumpaa shoppailua varten.",
  },
  legal: legalMessagesFi,
  faq: pagesMessagesFi.faq,
  support: pagesMessagesFi.support,
  advertising: pagesMessagesFi.advertising,
} satisfies Messages;
